const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

export interface CreateGameResponse {
  gameCode: string;
}

export interface JoinGameResponse {
  success: boolean;
  playerId: string;
}

export interface GuessResult {
  guessed: number;
  actual: number;
  correct: boolean;
}

export interface ScoreResult {
  score: number;
  totalPlayers: number;
  correctSequence: number[];
  direction: 'increasing' | 'decreasing';
}

export enum Language {
  RU = "RU", 
  RO = "RO", 
  EN = "EN", 
  SP = "SP", 
  PR = "PR"
}

export interface Situation extends Record<Language, string> {
  id: string;
}

export interface GameState {
  gameCode: string;
  state: string;
  players: Player[];
  isHost: boolean;
  currentSituationPlayer: string | null;
  assignedNumber?: number;
  currentSituation?: Situation;
  guesses?: { [playerId: string]: number };
  results?: { [playerId: string]: GuessResult };
  scoreResult?: ScoreResult;
}

export interface Player {
  id: string;
  name: string;
  joinedAt: string;
}

class ApiService {
  async createGame(): Promise<CreateGameResponse> {
    const response = await fetch(`${API_BASE_URL}/games`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to create game');
    }

    return await response.json();
  }

  async joinGame(gameCode: string, playerId: string, playerName: string): Promise<JoinGameResponse> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playerId, playerName }),
    });

    if (!response.ok) {
      throw new Error('Failed to join game');
    }

    return await response.json();
  }

  async getGameState(gameCode: string, playerId: string): Promise<GameState> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/state?playerId=${playerId}`);

    if (!response.ok) {
      throw new Error('Failed to get game state');
    }

    return await response.json();
  }

  async advanceGame(gameCode: string, playerId: string): Promise<{ success: boolean; gameState: GameState }> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/advance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playerId }),
    });

    if (!response.ok) {
      throw new Error('Failed to advance game');
    }

    return await response.json();
  }


  async submitGuesses(gameCode: string, playerId: string, guesses: { [playerId: string]: number }): Promise<{ success: boolean; gameState: GameState }> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/guesses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playerId, guesses }),
    });

    if (!response.ok) {
      throw new Error('Failed to submit guesses');
    }

    return await response.json();
  }

  async getRandomSituations(gameCode: string, userId: string): Promise<Situation[]> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/get-random-situations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error('Failed to get random topics');
    }

    const {situations} = await response.json();

    return situations
  }

  async setGameSituation(gameCode: string, userId: string, situationId: string): Promise<{ success: boolean; gameState: GameState }> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/set-game-situation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, situationId }),
    });

    if (!response.ok) {
      throw new Error('Failed to set game topic');
    }

    return await response.json();
  }

  async changePlayerName(gameCode: string, targetUserId: string, requestingUserId: string, newName: string): Promise<{ success: boolean; gameState: GameState }> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/players/${targetUserId}/change-name`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId: requestingUserId, newName }),
    });

    if (!response.ok) {
      throw new Error('Failed to change player name');
    }

    return await response.json();
  }

  async removePlayer(gameCode: string, targetUserId: string, requestingUserId: string): Promise<{ success: boolean; gameState: GameState }> {
    const response = await fetch(`${API_BASE_URL}/games/${gameCode}/players/${targetUserId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId: requestingUserId }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove player');
    }

    return await response.json();
  }

}

export const apiService = new ApiService();